import { useRef, useState } from 'react'
import { CAMERA_PRESETS } from '../camera/presets'
import {
  downloadStillPng,
  getRecorderSupport,
  startRecording,
  type ActiveRecording,
  type RecordQuality,
} from '../recording/capture'
import { getPoseById } from '../poses/library'
import { useAppStore } from '../store'
import type { CardsMode, LayerFlags } from '../types'

const LAYER_META: { key: keyof LayerFlags; label: string; available: boolean; note?: string }[] = [
  { key: 'muscles', label: 'Muscle highlights', available: true },
  { key: 'skeleton', label: 'Skeleton', available: true },
  { key: 'spine', label: 'Spine markers', available: true },
  { key: 'organs', label: 'Organs (digestive, decorative)', available: true, note: 'Schematic only' },
  { key: 'circulation', label: 'Circulation (decorative)', available: true, note: 'Not physiological flow' },
  { key: 'respiratory', label: 'Respiratory (decorative)', available: true },
  { key: 'pelvic', label: 'Pelvic / reproductive (schematic)', available: true, note: 'No genital mesh on rig' },
]

export function ControlPanel() {
  const cameraPreset = useAppStore((s) => s.cameraPreset)
  const setCameraPreset = useAppStore((s) => s.setCameraPreset)
  const cardsMode = useAppStore((s) => s.cardsMode)
  const setCardsMode = useAppStore((s) => s.setCardsMode)
  const layers = useAppStore((s) => s.layers)
  const setLayer = useAppStore((s) => s.setLayer)
  const showGrid = useAppStore((s) => s.showGrid)
  const setShowGrid = useAppStore((s) => s.setShowGrid)
  const recording = useAppStore((s) => s.recording)
  const recordProgress = useAppStore((s) => s.recordProgress)
  const setRecording = useAppStore((s) => s.setRecording)
  const setRecordProgress = useAppStore((s) => s.setRecordProgress)
  const setControlsLocked = useAppStore((s) => s.setControlsLocked)
  const poseId = useAppStore((s) => s.poseId)
  const locked = useAppStore((s) => s.controlsLocked)
  const setShowAbout = useAppStore((s) => s.setShowAbout)

  const [quality, setQuality] = useState<RecordQuality>('720p')
  const [error, setError] = useState<string | null>(null)
  const active = useRef<ActiveRecording | null>(null)
  const savedPreset = useRef(cameraPreset)
  const support = getRecorderSupport()

  const begin = (mode: 'tour' | 'single', durationSec: number) => {
    setError(null)
    try {
      savedPreset.current = useAppStore.getState().cameraPreset
      setControlsLocked(true)
      setRecording(true)
      setRecordProgress(0)
      active.current = startRecording({
        quality,
        mode,
        durationSec,
        onProgress: (p) => {
          setRecordProgress(p)
          if (p >= 1) {
            setRecording(false)
            setControlsLocked(false)
            setCameraPreset(savedPreset.current)
            active.current = null
          }
        },
        setCameraPreset,
        getPoseTitle: () => {
          const p = getPoseById(useAppStore.getState().poseId)
          return p ? `${p.english} (${p.sanskrit})` : 'Pose'
        },
      })
    } catch (e) {
      setRecording(false)
      setControlsLocked(false)
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  const stop = () => {
    active.current?.stop()
    active.current = null
    setRecording(false)
    setControlsLocked(false)
    setCameraPreset(savedPreset.current)
  }

  return (
    <aside className="panel control-panel">
      <div className="panel-header">
        <h2>Controls</h2>
        <button type="button" className="linkish" onClick={() => setShowAbout(true)}>About / Limits</button>
      </div>

      <section>
        <h3>Camera</h3>
        <div className="btn-row wrap">
          {CAMERA_PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={cameraPreset === p.id ? 'active-toggle' : ''}
              disabled={locked}
              onClick={() => setCameraPreset(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
        <label className="check">
          <input type="checkbox" checked={showGrid} disabled={locked} onChange={(e) => setShowGrid(e.target.checked)} />
          Subtle grid (no opaque floor)
        </label>
      </section>

      <section>
        <h3>Muscle cards</h3>
        <select className="select" value={cardsMode} disabled={locked} onChange={(e) => setCardsMode(e.target.value as CardsMode)}>
          <option value="working-stabilizing">Working + stabilizing</option>
          <option value="all">All roles</option>
          <option value="off">Off</option>
        </select>
      </section>

      <section>
        <h3>Layers</h3>
        {LAYER_META.map((L) => (
          <label key={L.key} className="check">
            <input
              type="checkbox"
              checked={layers[L.key]}
              disabled={locked || !L.available}
              onChange={(e) => setLayer(L.key, e.target.checked)}
            />
            {L.label}
            {L.note && <span className="muted small"> — {L.note}</span>}
          </label>
        ))}
      </section>

      <section>
        <h3>Video export</h3>
        <p className="muted small">Recorder: {support.ok ? support.label : 'unsupported'} (MP4 if allowed; else WebM)</p>
        <label className="check">
          Quality
          <select className="select" value={quality} disabled={locked} onChange={(e) => setQuality(e.target.value as RecordQuality)}>
            <option value="720p">720p</option>
            <option value="1080p">1080p</option>
          </select>
        </label>
        <button type="button" className="primary record-btn" disabled={locked && !recording} onClick={() => (recording ? stop() : begin('tour', 60))}>
          {recording ? `Stop & save (${Math.round(recordProgress * 100)}%)` : 'Record 1-minute video'}
        </button>
        <div className="btn-row wrap">
          <button type="button" disabled={recording} onClick={() => begin('single', 15)}>Single-angle 15s</button>
          <button
            type="button"
            disabled={recording}
            onClick={() => {
              try { downloadStillPng(`yoga-anatomy-${poseId}.png`) }
              catch (e) { setError(e instanceof Error ? e.message : String(e)) }
            }}
          >
            Still PNG
          </button>
        </div>
        {recording && <div className="progress"><div style={{ width: `${recordProgress * 100}%` }} /></div>}
        {error && <p className="error">{error}</p>}
      </section>
    </aside>
  )
}
